import { ArrowRight, ChevronDown, ExternalLink, FileText, Building2, HeartPulse, ArrowRightLeft, Repeat, Stethoscope, BedDouble, Siren, Scissors, ScanLine, PhoneCall, Mic, Upload, ClipboardList, Database, FileImage } from "lucide-react"
import ContactDialog from "@/components/ContactDialog"
import { motion, AnimatePresence } from "framer-motion"
import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"

const HERO_FLIPS = [
  "patient's unique story",
  "handoff in healthcare",
]

const PRICING_FLIPS = [
  "One API = entire care journey",
]

const SERIF = { fontFamily: "'Crimson Pro', serif" }
const SANS  = { fontFamily: "'Manrope', sans-serif" }

type ThemeId = "light" | "snow" | "dark"

const THEMES: Record<ThemeId, Record<string, string>> = {
  light: {
    "--bg-page":        "#FAF9F6",
    "--bg-card":        "#FFFFFF",
    "--bg-section":     "#F3F2ED",
    "--bg-inverse":     "#2A2A2A",
    "--bg-inv-card":    "#1A1A1A",
    "--text-primary":   "#2A2A2A",
    "--text-secondary": "#5A5A5A",
    "--text-muted":     "#7A7A7A",
    "--text-inverse":   "#FAF9F6",
    "--text-inv-muted": "#C5C5C5",
    "--accent":         "#5A7D7C",
    "--accent-sub":     "rgba(90,125,124,0.10)",
    "--accent-bdr":     "rgba(90,125,124,0.20)",
    "--accent-bdr-hv":  "rgba(90,125,124,0.40)",
    "--border":         "#E5E3DC",
    "--btn-bg":         "#2A2A2A",
    "--btn-txt":        "#FAF9F6",
    "--btn-hv":         "#3A3A3A",
    "--code-bg":        "#1A1A1A",
    "--code-bdr":       "#3A3A3A",
    "--code-kw":        "#5A7D7C",
    "--code-str":       "#A8C5A4",
    "--code-txt":       "#C5C5C5",
    "--code-cmt":       "#7A7A7A",
  },
  snow: {
    "--bg-page":        "#FFFFFF",
    "--bg-card":        "#F2F1ED",
    "--bg-section":     "#FAFAF8",
    "--bg-inverse":     "#141414",
    "--bg-inv-card":    "#0C0C0C",
    "--text-primary":   "#111111",
    "--text-secondary": "#444444",
    "--text-muted":     "#666666",
    "--text-inverse":   "#F5F5F5",
    "--text-inv-muted": "#999999",
    "--accent":         "#3D6B6A",
    "--accent-sub":     "rgba(61,107,106,0.07)",
    "--accent-bdr":     "rgba(61,107,106,0.14)",
    "--accent-bdr-hv":  "rgba(61,107,106,0.32)",
    "--border":         "#E8E6E0",
    "--btn-bg":         "#111111",
    "--btn-txt":        "#FFFFFF",
    "--btn-hv":         "#222222",
    "--code-bg":        "#0C0C0C",
    "--code-bdr":       "#1E1E1E",
    "--code-kw":        "#3D6B6A",
    "--code-str":       "#8DB08A",
    "--code-txt":       "#D8D8D8",
    "--code-cmt":       "#555555",
  },
  dark: {
    "--bg-page":        "#0D0D0D",
    "--bg-card":        "#181818",
    "--bg-section":     "#111111",
    "--bg-inverse":     "#050505",
    "--bg-inv-card":    "#0A0A0A",
    "--text-primary":   "#EFEFEF",
    "--text-secondary": "#9A9A9A",
    "--text-muted":     "#525252",
    "--text-inverse":   "#EFEFEF",
    "--text-inv-muted": "#7A7A7A",
    "--accent":         "#6B9E9D",
    "--accent-sub":     "rgba(107,158,157,0.12)",
    "--accent-bdr":     "rgba(107,158,157,0.25)",
    "--accent-bdr-hv":  "rgba(107,158,157,0.50)",
    "--border":         "#252525",
    "--btn-bg":         "#EFEFEF",
    "--btn-txt":        "#0D0D0D",
    "--btn-hv":         "#D5D5D5",
    "--code-bg":        "#050505",
    "--code-bdr":       "#1A1A1A",
    "--code-kw":        "#6B9E9D",
    "--code-str":       "#85A881",
    "--code-txt":       "#B8B8B8",
    "--code-cmt":       "#434343",
  },
}

const THEME_LABELS: Record<ThemeId, string> = {
  light: "Light",
  snow:  "SNOW",
  dark:  "Dark",
}

const summaryTypes = [
  { moment: "Before the appointment",    name: "Pre-Visit Summary",       recipient: "Doctor, Nurse",                 icon: FileText,       description: "Context before consultation. Saves doctor time, prepares patients better.", delay: 0.1 },
  { moment: "During consultation",       name: "In-Consult Summary", recipient: "Doctor, Patient",               icon: HeartPulse,     description: "Doctor-patient conversation translated to a digital prescription.",          delay: 0.2 },
  { moment: "Moving between providers",  name: "Referral Summary",        recipient: "Patient, Receiving Specialist", icon: ArrowRightLeft, description: "Seamless care transitions between specialists and facilities.",           delay: 0.3 },
  { moment: "Between care teams",        name: "Handover Summary",        recipient: "Incoming Doctor, Nurse",        icon: Repeat,         description: "Between shifts, departments or teams for continuity and patient safety.",           delay: 0.4 },
  { moment: "Leaving the hospital",      name: "Discharge Summary",       recipient: "Insurer (TPA), Doctor, Patient",icon: Building2,      description: "Patients can leave early once doctor decides & for early insurance approval.",              delay: 0.5 },
  { moment: "After care is delivered",   name: "Follow-Up Summary",       recipient: "Doctor",                        icon: FileText,       description: "Post-procedure follow-up continuity.",           delay: 0.6 },
]

const problemScenarios = [
  { icon: Stethoscope, title: "Clinics",            pain: "Doctors spend consultation time asking for history because past reports are scattered or unavailable." },
  { icon: Building2,   title: "Hospitals",           pain: "Discharges delayed while someone manually collates labs, medications, procedures, and nursing notes." },
  { icon: BedDouble,   title: "Critical / Emergency Care", pain: "Handovers are important and could be bettered for doctors and nurses with all the critical details summarised easily." },
  { icon: Scissors,    title: "Procedures",          pain: "Surgeons benefit from the most updated patient picture just before the procedures." },
  { icon: ScanLine,    title: "Radiology",           pain: "Radiologists perform and interpret scans much better with the needed clinical context behind referral and patients can be better prepared for scans." },
  { icon: PhoneCall,   title: "Follow-ups",          pain: "Remote follow-ups rarely happen because collecting patient information post-discharge is too hard." },
]

const audiences = [
  { title: "Clinics & Hospitals",          description: "No IT team or small in-house IT teams looking for easy-to-integrate tools that can work quickly and at scale as needed." },
  { title: "Telehealth Platforms",         description: "Automating pre-call and prescriptions for better prepared patients and busy doctors helping consultations to be completed conveniently and in time." },
  { title: "Healthcare SaaS & HIT Vendors",description: "Embedding summarization into existing products without building tech and maintaining infrastructure yourself." },
  { title: "Digital Health Startups",      description: "Use cost-efficient, production-ready tools for all types of summarization needs, across specialities, without need to build the stack from scratch." },
]

type DemoSource = { type: "file" | "voice" | "form" | "ehr" | "note" | "image"; label: string }
type ReportSection = { heading: string; content: string }
type DemoReport = {
  letterhead: { name: string; line1: string; line2?: string }
  title: string
  subline?: string
  meta: { label: string; value: string }[]
  body: ReportSection[]
  history?: { year: string; text: string }[]
  records?: string[]
  signature?: { name: string; creds: string; reg?: string; side: "left" | "right" }
  referredTo?: string
}
type DemoScenario = {
  label: string
  sources: DemoSource[]
  messyNote: string
  json: string
  report: DemoReport
  latency: string
}

const DEMO_SCENARIOS: DemoScenario[] = [
  {
    label: "Pre-Visit Summary",
    latency: "~55 sec",
    sources: [
      { type: "voice", label: "Patient voice note (2:34)" },
      { type: "file", label: "Blood_Report_Jan2025.pdf" },
      { type: "file", label: "ECG_Report_2024.pdf" },
      { type: "image", label: "Old_Prescription_photo.jpg" },
      { type: "form", label: "Online intake form" },
    ],
    messyNote: `"...chest mein dard ho raha hai 2 din se...
left haath mein bhi jaata hai... sugar ki
dawai le raha hoon... koi allergy hai
sulfa drugs se..."

Intake form: Age 45, Male, Diabetic
Uploaded: blurry prescription photo
  showing Metformin 500mg, Amlodipine 5mg`,
    json: `{
  "triage_tier": "urgent",
  "flagged_items": ["Sulfa allergy",
    "Uncontrolled HbA1c 8.2"],
  "suggested_investigations":
    ["Troponin", "ECG", "2D Echo"]
}`,
    report: {
      letterhead: {
        name: "APEX CARDIAC CLINIC",
        line1: "Road No. 36, Jubilee Hills",
        line2: "Hyderabad 500033 \u00b7 +91 40 2354 7788",
      },
      title: "PRE-VISIT BRIEF",
      subline: "For Dr. R. Iyer\u2019s consultation \u00b7 22 Apr 2026",
      meta: [
        { label: "Patient", value: "Rajesh Kumar \u00b7 45M \u00b7 MRN 24-8821" },
        { label: "Appointment", value: "22 Apr 2026 \u00b7 10:30 AM" },
      ],
      body: [
        { heading: "Presenting complaint", content: "Uneasiness since 1 day. Wants a cardiac check-up." },
      ],
      history: [
        { year: "2021", text: "Sulfa drug reaction \u2014 rash, facial swelling. Advised lifelong avoidance." },
        { year: "2023", text: "Routine ECG \u2014 normal sinus rhythm. Lipid panel: LDL 142, TG 188." },
        { year: "2024", text: "HbA1c drift to 8.2 \u00b7 counselled on diet. ECG repeated \u2014 normal." },
        { year: "2026", text: "Current \u2014 uneasiness x1 day." },
      ],
      records: [
        "Blood Report \u00b7 Jan 2025",
        "ECG Report \u00b7 2024",
        "ECG Report \u00b7 2023",
        "Old Prescription (Metformin, Amlodipine) \u00b7 2020",
      ],
    },
  },
  {
    label: "Discharge Summary",
    latency: "~1 min 40 sec",
    sources: [
      { type: "file", label: "Admission_Notes.pdf" },
      { type: "ehr", label: "Lab results (LIS)" },
      { type: "file", label: "Radiology_CXR.pdf" },
      { type: "file", label: "Nursing_Notes_D1-D3.pdf" },
      { type: "file", label: "Drug_Chart.pdf" },
      { type: "note", label: "Doctor's round notes" },
    ],
    messyNote: `Adm: 18/04 — SOB, fever 102F, cough
productive. CXR: Rt lower lobe consolidation

Labs D1: WBC 14.2, CRP 86, SpO2 92%
Labs D3: WBC 8.1, CRP 22, SpO2 97%
Rx: Inj Ceftriaxone 1g BD, PCM PRN
Nurse notes: "pt improving, afebrile D2
  onwards, tolerating oral feeds"
Sputum cx: Strep pneumoniae`,
    json: `{
  "diagnosis": "Community-acquired
    pneumonia (Strep pneumoniae)",
  "discharge_meds": [
    "Amoxicillin 500mg TDS x5d",
    "Paracetamol 500mg PRN"],
  "follow_up": "OPD in 7 days"
}`,
    report: {
      letterhead: {
        name: "STERLING MULTISPECIALTY HOSPITAL",
        line1: "HSR Layout, Sector 2, Bengaluru 560102",
        line2: "24\u00d77 Emergency \u00b7 +91 80 4112 9900",
      },
      title: "DISCHARGE SUMMARY",
      subline: "IP No. SMH-2026-04821 \u00b7 Ward 3B",
      meta: [
        { label: "Patient", value: "Arun Desai \u00b7 42M \u00b7 MRN 18-9142" },
        { label: "Admitted", value: "18 Apr 2026" },
        { label: "Discharged", value: "21 Apr 2026" },
        { label: "Under care of", value: "Dr. Priya Nair, Pulmonology" },
      ],
      body: [
        { heading: "Final diagnosis", content: "Community-acquired pneumonia (Strep pneumoniae), right lower lobe \u2014 resolved." },
        { heading: "Hospital course", content: "Admitted with fever 102\u00b0F, SOB, productive cough. Started on IV Ceftriaxone 1g BD after blood and sputum cultures. Afebrile from D2. WBC and CRP normalised by D3. Stepped down to oral antibiotics on D3." },
        { heading: "At discharge", content: "Afebrile \u00b7 SpO\u2082 97% on room air \u00b7 WBC within normal limits." },
        { heading: "Medications", content: "Amoxicillin 500mg TDS x5 days \u00b7 Paracetamol 500mg PRN." },
        { heading: "Follow-up", content: "OPD review in 7 days \u00b7 Repeat CXR if symptoms recur." },
        { heading: "Instructions", content: "Return immediately if: Fever >101\u00b0F \u00b7 Breathlessness \u00b7 Chest pain \u00b7 Persistent cough beyond 10 days." },
      ],
      signature: {
        name: "Dr. Priya Nair",
        creds: "MBBS, MD, DM (Pulmonology)",
        reg: "Reg. KMC 38112",
        side: "left",
      },
    },
  },
  {
    label: "Referral Summary",
    latency: "~45 sec",
    sources: [
      { type: "note", label: "GP consultation notes" },
      { type: "file", label: "ECG_Report.pdf" },
      { type: "ehr", label: "Medication history (EHR)" },
      { type: "file", label: "Previous_BP_Log.xlsx" },
      { type: "image", label: "Handwritten_referral_slip.jpg" },
    ],
    messyNote: `Dr Mehta's scribble: "Ref to Cardio —
new SOB on exertion x2wk. HTN 10yr.
ECG: ST dep V4-V6. Fam h/o — father
MI at 52. Smoker."

EHR shows: Telmisartan 40mg, ASA 75mg
BP log: 148/92, 152/88, 144/90 (last 3)`,
    json: `{
  "urgency": "priority",
  "key_findings": [
    "ST depression V4-V6",
    "Smoker + family h/o CAD"],
  "recommended": ["Stress test",
    "Lipid panel", "2D Echo"]
}`,
    report: {
      letterhead: {
        name: "SAXENA FAMILY CLINIC",
        line1: "General Practice \u00b7 Gomti Nagar, Lucknow",
        line2: "Reg. UPMC/2018/11422 \u00b7 +91 522 4067 2210",
      },
      title: "REFERRAL SUMMARY",
      subline: "21 Apr 2026",
      referredTo: "Department of Cardiology, Sanjeevani Heart Institute, Lucknow",
      meta: [
        { label: "Patient", value: "Sunita Rao \u00b7 58F" },
        { label: "Urgency", value: "Priority" },
      ],
      body: [
        { heading: "Reason for referral", content: "New onset exertional dyspnoea x2 weeks with ischaemic ECG changes." },
        { heading: "History", content: "HTN x10y \u00b7 Active smoker \u00b7 Father \u2014 MI at 52." },
        { heading: "Key findings", content: "ST depression V4-V6 \u00b7 BP poorly controlled (avg 148/90)." },
        { heading: "Current medications", content: "Telmisartan 40mg OD \u00b7 Aspirin 75mg OD." },
        { heading: "Notes", content: "Requesting specialist evaluation \u2014 stress test, lipid panel, 2D Echo as deemed appropriate." },
      ],
      signature: {
        name: "Dr. Anil Saxena",
        creds: "MBBS, MD (Int. Med)",
        reg: "Reg. UPMC 11422",
        side: "right",
      },
    },
  },
]

const SOURCE_ICONS: Record<DemoSource["type"], typeof FileText> = {
  file: FileText,
  voice: Mic,
  form: ClipboardList,
  ehr: Database,
  note: FileText,
  image: FileImage,
}

const SOURCE_COLORS: Record<DemoSource["type"], string> = {
  file: "#E67E22",
  voice: "#8E44AD",
  form: "#2980B9",
  ehr: "#27AE60",
  note: "#7F8C8D",
  image: "#E74C3C",
}

export default function LandingPage() {
  const navigate = useNavigate()
  const [hoveredCard, setHoveredCard] = useState<number | null>(null)
  const [heroIndex, setHeroIndex] = useState(0)
  const [demoIndex, setDemoIndex] = useState(0)
  const [demoRevealed, setDemoRevealed] = useState(false)
  const [demoOutputView, setDemoOutputView] = useState<"json" | "report">("json")
  const [mobilePhase, setMobilePhase] = useState<"input" | "output">("input")
  const [mobileProgress, setMobileProgress] = useState(0)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  const tabBarRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const btn = tabRefs.current[demoIndex]
    const bar = tabBarRef.current
    if (!btn || !bar) return
    // Horizontal-only scroll within the tab bar — does not affect page scroll
    const target = btn.offsetLeft - bar.clientWidth / 2 + btn.clientWidth / 2
    bar.scrollTo({ left: target, behavior: "smooth" })
  }, [demoIndex])
  const [pricingFlipIndex, setPricingFlipIndex] = useState(0)
  const [hoveredTier, setHoveredTier] = useState<number | null>(null)

  // "Try SNOW" dropdown in the header — Cloud + API options
  const [trySnowOpen, setTrySnowOpen] = useState(false)
  const trySnowRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!trySnowOpen) return
    function onDocClick(e: MouseEvent) {
      if (!trySnowRef.current?.contains(e.target as Node)) setTrySnowOpen(false)
    }
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setTrySnowOpen(false) }
    document.addEventListener("click", onDocClick)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("click", onDocClick)
      document.removeEventListener("keydown", onKey)
    }
  }, [trySnowOpen])

  // Single-TLD architecture. snowmed.io 301s into .health at the edge.
  const SNOW_CLOUD_URL   = "https://summarisenow.snowmed.health/login"
  const SNOW_API_URL     = "https://platform.snowmed.health/login"
  const SNOW_DESKTOP_URL = "https://snowmed.health/download"

  // Contact dialog (Hospital / Enterprise tier CTA + final-CTA escape hatch)
  const [contactOpen, setContactOpen] = useState(false)
  const [contactTier, setContactTier] = useState<string | undefined>(undefined)
  const openContact = (tier?: string) => { setContactTier(tier); setContactOpen(true) }

  useEffect(() => {
    const interval = setInterval(() => setPricingFlipIndex(i => (i + 1) % PRICING_FLIPS.length), 3500)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => setHeroIndex(i => (i + 1) % HERO_FLIPS.length), 4000)
    return () => clearInterval(interval)
  }, [])

  // Cycle demo scenarios — 9s per scenario (4s input → 2s JSON → 3s Report)
  useEffect(() => {
    const SCENARIO_MS = 11000
    const INPUT_MS = 4000
    const JSON_MS = 2000

    const runOutputPhase = () => {
      setDemoRevealed(true)
      setDemoOutputView("json")
      setMobilePhase("output")
      setTimeout(() => setDemoOutputView("report"), JSON_MS)
    }

    const cycle = setInterval(() => {
      setDemoRevealed(false)
      setDemoOutputView("json")
      setMobilePhase("input")
      setTimeout(() => {
        setDemoIndex(i => (i + 1) % DEMO_SCENARIOS.length)
        setTimeout(runOutputPhase, INPUT_MS - 700)
      }, 400)
    }, SCENARIO_MS)

    // First scenario
    const initial = setTimeout(runOutputPhase, INPUT_MS)

    // Progress bar for mobile (updates every 50ms across the 9s cycle)
    let elapsed = 0
    const tick = setInterval(() => {
      elapsed = (elapsed + 50) % SCENARIO_MS
      setMobileProgress((elapsed / SCENARIO_MS) * 100)
    }, 50)

    return () => { clearInterval(cycle); clearTimeout(initial); clearInterval(tick) }
  }, [])

  const [theme, setTheme] = useState<ThemeId>(() => {
    return (localStorage.getItem("snow-theme") as ThemeId) ?? "snow"
  })

  useEffect(() => {
    localStorage.setItem("snow-theme", theme)
  }, [theme])

  const t = THEMES[theme]

  const css = (extra?: Record<string, string>): React.CSSProperties =>
    ({ ...t, ...extra } as React.CSSProperties)

  const demo = DEMO_SCENARIOS[demoIndex]

  return (
    <div style={css({ background: "var(--bg-page)", color: "var(--text-primary)", ...SANS })}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <header
        className="border-b backdrop-blur-sm sticky top-0 z-50"
        style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-page)", ...t } as React.CSSProperties}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            style={{ ...SERIF, color: "var(--text-primary)" }}
            className="tracking-tight text-[1.5rem] sm:text-[2rem] font-semibold cursor-pointer hover:opacity-80 transition-opacity bg-transparent border-0 p-0"
            aria-label="Back to top"
          >
            SNOW
          </button>
          <div className="flex items-center gap-6">
            <a href="#journey" className="text-sm transition-colors hidden sm:block" style={{ color: "var(--text-secondary)" }}>
              How it helps
            </a>
            <a href="#pricing" className="text-sm transition-colors hidden sm:block" style={{ color: "var(--text-secondary)" }}>
              Pricing
            </a>
            <div className="relative" ref={trySnowRef}>
              <button
                type="button"
                aria-haspopup="true"
                aria-expanded={trySnowOpen}
                onClick={(e) => { e.stopPropagation(); setTrySnowOpen(o => !o) }}
                className="inline-flex items-center gap-1.5 px-4 sm:px-5 py-2 sm:py-2.5 text-sm transition-all duration-300 shadow-sm"
                style={{ background: "var(--btn-bg)", color: "var(--btn-txt)" }}
                onMouseEnter={e => (e.currentTarget.style.background = "var(--btn-hv)")}
                onMouseLeave={e => (e.currentTarget.style.background = "var(--btn-bg)")}
              >
                Try SNOW
                <ChevronDown
                  className="w-3.5 h-3.5 transition-transform"
                  style={{ transform: trySnowOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                />
              </button>
              {trySnowOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-72 rounded-lg shadow-lg ring-1 ring-black/5 overflow-hidden z-[60]"
                  style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
                >
                  <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>
                    Products
                  </div>
                  <a
                    href={SNOW_CLOUD_URL}
                    role="menuitem"
                    className="flex items-start gap-3 px-4 py-3 hover:opacity-80 transition-opacity"
                    style={{ color: "var(--text-primary)" }}
                  >
                    <div className="flex-1">
                      <div className="text-sm font-semibold">SNOW Web</div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                        Ready-to-use hosted summarisation service for doctors & hospitals. No setup.
                      </div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 mt-1" style={{ color: "var(--text-muted)" }} />
                  </a>
                  <a
                    href={SNOW_API_URL}
                    role="menuitem"
                    className="flex items-start gap-3 px-4 py-3 hover:opacity-80 transition-opacity"
                    style={{ color: "var(--text-primary)", borderTop: "1px solid var(--border)" }}
                  >
                    <div className="flex-1">
                      <div className="text-sm font-semibold">SNOW Platform</div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                        Build summarisation into your own products using APIs.
                      </div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 mt-1" style={{ color: "var(--text-muted)" }} />
                  </a>
                  <a
                    href={SNOW_DESKTOP_URL}
                    role="menuitem"
                    className="flex items-start gap-3 px-4 py-3 hover:opacity-80 transition-opacity"
                    style={{ color: "var(--text-primary)", borderTop: "1px solid var(--border)" }}
                  >
                    <div className="flex-1">
                      <div className="text-sm font-semibold">SNOW Desk</div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                        Drag-and-drop desktop app for clinics & hospitals to upload bulk documents.
                      </div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 mt-1" style={{ color: "var(--text-muted)" }} />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-12 sm:pt-24 pb-8 sm:pb-16">
        <div className="max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div
              className="inline-block px-4 py-1.5 mb-6 sm:mb-8 border text-xs sm:text-sm tracking-wide font-semibold"
              style={{ background: "var(--accent-sub)", color: "var(--accent)", borderColor: "var(--accent-bdr)" }}
            >
              HEALTH SUMMARIZATION API
            </div>
            <h1 className="text-[2.5rem] sm:text-[3rem] lg:text-[4rem] leading-[1.1] mb-4 sm:mb-6" style={{ ...SERIF, color: "var(--text-primary)" }}>
              Every{" "}
              <span className="inline-block relative overflow-hidden align-bottom" style={{ minWidth: "8ch" }}>
                <AnimatePresence mode="wait">
                  <motion.span
                    key={heroIndex}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ duration: 0.5 }}
                    className="inline-block font-bold"
                    style={{ color: "var(--accent)" }}
                  >
                    {HERO_FLIPS[heroIndex]},
                  </motion.span>
                </AnimatePresence>
              </span>
              {" "}summarized
            </h1>

            {/* Context paragraph + one-liner */}
            <p className="text-base sm:text-lg leading-relaxed mb-6 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
              Information is scattered in Healthcare. Getting patients' context summarised, at right time,
              in right format has been challenging. SNOW solves it.
            </p>
            <p className="text-lg sm:text-xl lg:text-2xl font-medium mb-6" style={{ color: "var(--text-primary)" }}>
              One API call. Structured summary. Under 2 minutes.
            </p>

            {/* 3 sharp bullets */}
            <ul className="space-y-2 mb-8 sm:mb-10">
              {[
                "All summary types — Consultations, Referrals, Procedures, Discharges, Follow-ups",
                "No expertise needed. No infrastructure to maintain",
              ].map((bullet) => (
                <li key={bullet} className="flex items-start gap-2 text-sm sm:text-base" style={{ color: "var(--text-secondary)" }}>
                  <span className="mt-1 flex-shrink-0" style={{ color: "var(--accent)" }}>&#10003;</span>
                  {bullet}
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row items-start sm:items-stretch gap-3">
              <div className="flex flex-col gap-1">
                <a
                  href={SNOW_CLOUD_URL}
                  className="px-6 sm:px-8 py-3 sm:py-4 flex items-center gap-2 group shadow-lg transition-all duration-300"
                  style={{ background: "var(--btn-bg)", color: "var(--btn-txt)" }}
                  onMouseEnter={e => (e.currentTarget.style.background = "var(--btn-hv)")}
                  onMouseLeave={e => (e.currentTarget.style.background = "var(--btn-bg)")}
                >
                  <span>Use SNOW Web</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <span className="text-[11px] tracking-wide text-center sm:text-left" style={{ color: "var(--text-muted)" }}>
                  For clinicians
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <a
                  href={SNOW_API_URL}
                  className="px-6 sm:px-8 py-3 sm:py-4 flex items-center gap-2 transition-all duration-300 border"
                  style={{ background: "transparent", color: "var(--text-primary)", borderColor: "var(--text-primary)" }}
                >
                  <span>Build with SNOW Platform</span>
                </a>
                <span className="text-[11px] tracking-wide text-center sm:text-left" style={{ color: "var(--text-muted)" }}>
                  For developers
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ── Live API Demo ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 sm:mt-16"
        >
          <div className="border overflow-hidden max-w-4xl" style={{ background: "var(--bg-card)", borderColor: "var(--border)" }}>
            {/* Mobile-only top progress bar */}
            <div className="md:hidden h-0.5 w-full" style={{ background: "var(--border)" }}>
              <div
                className="h-full transition-all"
                style={{ width: `${mobileProgress}%`, background: "var(--accent)" }}
              />
            </div>
            {/* Demo tabs */}
            <div ref={tabBarRef} className="flex items-center gap-1 px-4 py-3 border-b overflow-x-auto justify-start sm:justify-center" style={{ borderColor: "var(--border)" }}>
              {DEMO_SCENARIOS.map((s, i) => (
                <button
                  key={i}
                  ref={(el) => { tabRefs.current[i] = el }}
                  onClick={() => {
                    setDemoRevealed(false)
                    setDemoOutputView("json")
                    setTimeout(() => {
                      setDemoIndex(i)
                      setTimeout(() => {
                        setDemoRevealed(true)
                        setTimeout(() => setDemoOutputView("report"), 2000)
                      }, 300)
                    }, 200)
                  }}
                  className="px-3 py-1.5 text-xs tracking-wide transition-all whitespace-nowrap flex-shrink-0 border"
                  style={demoIndex === i
                    ? { background: "var(--text-primary)", color: "var(--bg-card)", borderRadius: 4, borderColor: "var(--text-primary)" }
                    : { color: "var(--text-muted)", borderColor: "transparent" }
                  }
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div className="grid md:grid-cols-2 relative h-[460px] md:h-[640px]">
              {/* Input — messy, realistic */}
              <div
                className={`p-5 sm:p-6 md:border-r overflow-hidden transition-opacity duration-500 md:opacity-100 md:static md:block ${mobilePhase === "input" ? "opacity-100" : "opacity-0 pointer-events-none"} absolute md:relative inset-0 md:inset-auto`}
                style={{ borderColor: "var(--border)" }}
              >
                <div className="text-xs uppercase tracking-wider mb-4 font-medium" style={{ color: "var(--text-muted)" }}>What goes in</div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`in-${demoIndex}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Source file pills */}
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {demo.sources.map((src, i) => {
                        const Icon = SOURCE_ICONS[src.type]
                        return (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.1 }}
                            className="flex items-center gap-1.5 px-2 py-1 text-[10px] sm:text-xs border"
                            style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}
                          >
                            <Icon className="w-3 h-3 flex-shrink-0" style={{ color: SOURCE_COLORS[src.type] }} />
                            <span className="truncate max-w-[140px]">{src.label}</span>
                          </motion.div>
                        )
                      })}
                    </div>
                    {/* Messy note */}
                    <pre
                      className="text-[11px] sm:text-xs leading-relaxed whitespace-pre-wrap p-3 border border-dashed"
                      style={{
                        color: "var(--text-secondary)",
                        borderColor: "var(--border)",
                        background: "var(--bg-section)",
                        fontFamily: "'Courier New', monospace",
                      }}
                    >
                      {demo.messyNote}
                    </pre>
                  </motion.div>
                </AnimatePresence>
              </div>
              {/* Output — JSON then Report */}
              <div
                className={`p-5 sm:p-6 transition-opacity duration-500 md:opacity-100 md:static md:block ${mobilePhase === "output" ? "opacity-100" : "opacity-0 pointer-events-none"} absolute md:relative inset-0 md:inset-auto`}
                style={{ background: "var(--bg-section)" }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="text-xs uppercase tracking-wider font-medium" style={{ color: "var(--text-muted)" }}>What comes out</div>
                  {demoRevealed && (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-xs px-2 py-0.5 font-semibold"
                      style={{ background: "var(--accent-sub)", color: "var(--accent)" }}
                    >
                      {demo.latency}
                    </motion.span>
                  )}
                  {/* JSON / Report toggle */}
                  {demoRevealed && (
                    <div className="ml-auto flex items-center gap-0.5 border p-0.5" style={{ borderColor: "var(--border)" }}>
                      {(["json", "report"] as const).map((v) => (
                        <button
                          key={v}
                          onClick={() => setDemoOutputView(v)}
                          className="px-2 py-1 text-[10px] sm:text-xs transition-all"
                          style={demoOutputView === v
                            ? { background: "var(--accent-sub)", color: "var(--accent)" }
                            : { color: "var(--text-muted)" }
                          }
                        >
                          {v === "json" ? "API Response" : "Report Preview"}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative overflow-y-auto h-[360px] md:h-[560px]">
                  <AnimatePresence mode="wait">
                    {!demoRevealed ? (
                      <motion.div
                        key={`loading-${demoIndex}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2 text-sm"
                        style={{ color: "var(--text-muted)" }}
                      >
                        <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ background: "var(--accent)" }} />
                        Processing...
                      </motion.div>
                    ) : demoOutputView === "json" ? (
                      <motion.pre
                        key={`json-${demoIndex}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="text-[11px] sm:text-xs leading-relaxed font-mono whitespace-pre-wrap"
                        style={{ color: "var(--accent)" }}
                      >
                        {demo.json}
                      </motion.pre>
                    ) : (
                      <motion.div
                        key={`report-${demoIndex}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="border shadow-sm px-5 py-4 text-[11px] leading-snug"
                        style={{ background: "#fdfcf8", borderColor: "#d6d3ca", color: "#2a2a28" }}
                      >
                        {/* Letterhead */}
                        <div className="text-center pb-2 border-b" style={{ borderColor: "#cfcbbf" }}>
                          <div className="text-[13px] font-semibold tracking-wide" style={{ ...SERIF, color: "#1a1a18" }}>{demo.report.letterhead.name}</div>
                          <div className="text-[10px]" style={{ color: "#6b6a64" }}>{demo.report.letterhead.line1}</div>
                          {demo.report.letterhead.line2 && (
                            <div className="text-[10px]" style={{ color: "#6b6a64" }}>{demo.report.letterhead.line2}</div>
                          )}
                        </div>

                        {/* Centered title */}
                        <div className="text-center py-3">
                          <div className="text-[13px] font-bold tracking-[0.15em]" style={{ ...SERIF, color: "#1a1a18" }}>{demo.report.title}</div>
                          {demo.report.subline && (
                            <div className="text-[10px] italic mt-0.5" style={{ color: "#6b6a64" }}>{demo.report.subline}</div>
                          )}
                        </div>

                        {/* Referred-to (referral only) */}
                        {demo.report.referredTo && (
                          <div className="mb-2 text-[10.5px]">
                            <span style={{ color: "#6b6a64" }}>To: </span>
                            <span style={{ color: "#2a2a28" }}>{demo.report.referredTo}</span>
                          </div>
                        )}

                        {/* Meta rows */}
                        <div className="space-y-0.5 mb-3">
                          {demo.report.meta.map((m, i) => (
                            <div key={i} className="flex gap-2 text-[10.5px]">
                              <span className="w-24 flex-shrink-0" style={{ color: "#6b6a64" }}>{m.label}</span>
                              <span style={{ color: "#2a2a28" }}>{m.value}</span>
                            </div>
                          ))}
                        </div>

                        {/* Body sections */}
                        <div className="space-y-2">
                          {demo.report.body.map((sec, i) => (
                            <div key={i}>
                              <div className="text-[9.5px] uppercase tracking-[0.1em] font-semibold mb-0.5" style={{ color: "#6b6a64" }}>{sec.heading}</div>
                              <div className="text-[10.5px] leading-relaxed" style={{ color: "#2a2a28" }}>{sec.content}</div>
                            </div>
                          ))}
                        </div>

                        {/* History timeline (pre-visit) */}
                        {demo.report.history && (
                          <div className="mt-3 space-y-1">
                            {demo.report.history.map((h, i) => (
                              <div key={i} className="flex gap-3 text-[10.5px]">
                                <span className="w-10 flex-shrink-0 font-semibold" style={{ color: "#1a1a18" }}>{h.year}</span>
                                <span style={{ color: "#2a2a28" }}>{h.text}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Records on file */}
                        {demo.report.records && (
                          <div className="mt-3 pt-2 border-t" style={{ borderColor: "#cfcbbf" }}>
                            <div className="text-[9.5px] uppercase tracking-[0.1em] font-semibold mb-1" style={{ color: "#6b6a64" }}>Records on file</div>
                            <ul className="text-[10.5px] space-y-0.5" style={{ color: "#2a2a28" }}>
                              {demo.report.records.map((r, i) => (
                                <li key={i}>&bull; {r}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Signature */}
                        {demo.report.signature && (
                          <div className={`mt-4 pt-3 text-[10px] ${demo.report.signature.side === "right" ? "text-right" : "text-left"}`}>
                            <div className="inline-block">
                              <div className="border-t pt-1" style={{ borderColor: "#6b6a64", minWidth: 140 }}>
                                <div className="font-semibold" style={{ color: "#1a1a18" }}>{demo.report.signature.name}</div>
                                <div style={{ color: "#6b6a64" }}>{demo.report.signature.creds}</div>
                                {demo.report.signature.reg && (
                                  <div style={{ color: "#6b6a64" }}>{demo.report.signature.reg}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
            {/* Mobile-only progress bar */}
            <div className="md:hidden h-0.5 w-full" style={{ background: "var(--border)" }}>
              <div
                className="h-full transition-all"
                style={{ width: `${mobileProgress}%`, background: "var(--accent)" }}
              />
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── The Problem Is Everywhere ─────────────────────────────────────── */}
      <section style={{ background: "var(--bg-section)" }} className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="max-w-3xl mb-12">
            <h2 className="text-[1.75rem] sm:text-[2.5rem] mb-4 leading-tight" style={{ ...SERIF, color: "var(--text-primary)" }}>
              While information exists,<br />Contextual summary is missing.
            </h2>
            <p className="text-base sm:text-[1.125rem] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              These aren't just documentation problems in healthcare. They're information assembly challenges as well.
              SNOW solves them — pulling from fragmented sources, structuring the right summary,
              delivering it at the right moment. One API call.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {problemScenarios.map((scenario, index) => {
              const Icon = scenario.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="border p-6 transition-all"
                  style={{ background: "var(--bg-card)", borderColor: "var(--border)" }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--accent-bdr-hv)")}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--border)")}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center flex-shrink-0" style={{ background: "var(--accent-sub)" }}>
                      <Icon className="w-4 h-4" style={{ color: "var(--accent)" }} />
                    </div>
                    <h3 className="text-base font-medium" style={{ color: "var(--text-primary)" }}>{scenario.title}</h3>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{scenario.pain}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── Care Journey Timeline ──────────────────────────────────────────── */}
      <section id="journey" className="max-w-7xl mx-auto px-5 sm:px-6 py-16 sm:py-20">
        <div className="mb-12 sm:mb-16">
          <h2 className="text-[1.75rem] sm:text-[2.5rem] mb-4" style={{ ...SERIF, color: "var(--text-primary)" }}>
            The complete care journey
          </h2>
          <p className="text-base sm:text-[1.125rem] max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            Several critical moments. One API. Built for how care actually moves.
          </p>
        </div>

        <div className="relative">
          <div
            className="absolute top-24 left-0 right-0 h-[2px] hidden lg:block"
            style={{ background: `linear-gradient(to right, var(--accent-sub), var(--accent-bdr), var(--accent-sub))` }}
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {summaryTypes.map((summary, index) => {
              const Icon = summary.icon
              const hovered = hoveredCard === index
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: summary.delay }}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                  className="relative"
                >
                  <div
                    className="absolute -top-2 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full border-2 items-center justify-center z-10 hidden lg:flex"
                    style={{ background: "var(--bg-page)", borderColor: "var(--accent)" }}
                  >
                    <Icon className="w-5 h-5" style={{ color: "var(--accent)" }} />
                  </div>
                  <div
                    className="border p-5 sm:p-6 mt-0 lg:mt-14 shadow-sm transition-all duration-300 h-full flex flex-col"
                    style={{
                      background: "var(--bg-card)",
                      borderColor: hovered ? "var(--accent-bdr-hv)" : "var(--border)",
                      transform: hovered ? "translateY(-4px)" : "none",
                      boxShadow: hovered ? "0 8px 24px rgba(0,0,0,0.12)" : undefined,
                    }}
                  >
                    <div className="flex items-start gap-3 mb-3 lg:hidden">
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ background: "var(--accent-sub)" }}>
                        <Icon className="w-5 h-5" style={{ color: "var(--accent)" }} />
                      </div>
                    </div>
                    <div className="text-xs uppercase tracking-wider mb-2 font-bold" style={{ color: "var(--accent)" }}>

                    </div>
                    <h3 className="text-[1.25rem] sm:text-[1.375rem] mb-2" style={{ ...SERIF, color: "var(--text-primary)" }}>
                      {summary.name}
                    </h3>
                    <p className="text-sm mb-3 flex-1" style={{ color: "var(--text-muted)" }}>{summary.description}</p>
                    <div className="pt-3 border-t mt-auto" style={{ borderColor: "var(--border)" }}>
                      <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                        <span className="font-medium">Delivered to:</span> {summary.recipient}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── Who We Serve ──────────────────────────────────────────────────── */}
      <section style={{ background: "var(--bg-section)" }} className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <h2 className="text-[1.75rem] sm:text-[2.5rem] mb-6 leading-tight" style={{ ...SERIF, color: "var(--text-primary)" }}>
                <span className="block text-[1.25rem] sm:text-[1.75rem]">Built for teams wanting care delivered better,</span>
                <span className="block">Conveniently, Correctly and Efficiently</span>
              </h2>
              <p className="text-base sm:text-[1.125rem] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                SNOW serves the low-code infrastructure layer of healthcare — the developers, IT teams, and
                platform builders who keep care flowing between systems, providers, and settings with focus on convenience, efficiency, and optimization of resources.
              </p>
            </div>
            <div className="grid gap-5">
              {audiences.map((audience, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="border p-5 sm:p-6 shadow-sm transition-all"
                  style={{ background: "var(--bg-card)", borderColor: "var(--border)" }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--accent-bdr-hv)")}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--border)")}
                >
                  <h3 className="text-base sm:text-[1.125rem] mb-2 font-medium" style={{ color: "var(--text-primary)" }}>
                    {audience.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                    {audience.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works (dark section) ───────────────────────────────────── */}
      <section style={{ background: "var(--bg-inverse)", color: "var(--text-inverse)" }} className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-[1.75rem] sm:text-[2.5rem] mb-4" style={SERIF}>
              Three steps. One endpoint.
            </h2>
            <p className="text-base sm:text-[1.125rem]" style={{ color: "var(--text-inv-muted)" }}>
              Integrate SNOW into your existing workflow. No infrastructure to maintain. No models to train.
            </p>
          </div>

          {/* 3 steps */}
          <div className="grid sm:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {[
              { step: "1", title: "Connect", desc: "Plug into your existing workflows for context from any sources — forms, voice, reports, EHR, notes." },
              { step: "2", title: "SNOW assembles", desc: "Pulls from fragmented sources, identifies the right summary type, structures the output." },
              { step: "3", title: "Receive", desc: "Structured JSON summary, ready to display or store. Under 2 minutes." },
            ].map((s) => (
              <div key={s.step} className="text-center sm:text-left">
                <div
                  className="inline-flex items-center justify-center w-10 h-10 mb-4 text-sm font-bold"
                  style={{ background: "var(--accent)", color: "var(--bg-inverse)" }}
                >
                  {s.step}
                </div>
                <h3 className="text-lg font-medium mb-2" style={{ color: "var(--text-inverse)" }}>{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--text-inv-muted)" }}>{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Code snippet */}
          <div
            className="p-5 sm:p-8 border font-mono text-sm overflow-x-auto shadow-2xl max-w-3xl mx-auto"
            style={{ background: "var(--code-bg)", borderColor: "var(--code-bdr)" }}
          >
            <div className="mb-4" style={{ color: "var(--code-cmt)" }}>{"// One call. Right summary. Right moment."}</div>
            <div className="space-y-1 text-xs sm:text-sm">
              <div>
                <span style={{ color: "var(--code-kw)" }}>curl </span>
                <span style={{ color: "var(--code-txt)" }}>-X POST </span>
                <span style={{ color: "var(--code-str)" }}>https://api.snowmed.io/v1/intake</span>
                <span style={{ color: "var(--code-txt)" }}> \</span>
              </div>
              <div className="ml-4">
                <span style={{ color: "var(--code-txt)" }}>-H </span>
                <span style={{ color: "var(--code-str)" }}>"Authorization: Bearer sn_live_YOUR_KEY"</span>
                <span style={{ color: "var(--code-txt)" }}> \</span>
              </div>
              <div className="ml-4">
                <span style={{ color: "var(--code-txt)" }}>-H </span>
                <span style={{ color: "var(--code-str)" }}>"Content-Type: application/json"</span>
                <span style={{ color: "var(--code-txt)" }}> \</span>
              </div>
              <div className="ml-4">
                <span style={{ color: "var(--code-txt)" }}>-d </span>
                <span style={{ color: "var(--code-str)" }}>{"'{ \"specialty\": \"general\", \"form_responses\": { ... } }'"}</span>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t" style={{ borderColor: "var(--code-bdr)", color: "var(--code-cmt)" }}>
              {"// → structured summary ready in <2s"}
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ───────────────────────────────────────────────────────── */}
      <section id="pricing" className="max-w-7xl mx-auto px-5 sm:px-6 py-16 sm:py-24">
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="text-[1.75rem] sm:text-[2.5rem] mb-4" style={{ ...SERIF, color: "var(--text-primary)" }}>
            Simple, Needs-based Tier pricing
          </h2>
          <p className="text-base sm:text-[1.125rem] mb-4" style={{ color: "var(--text-secondary)" }}>
            Start easy. Pay as per your needs.
          </p>
          <div className="inline-flex items-center justify-center relative overflow-hidden px-4 py-2 border" style={{ background: "var(--accent-sub)", borderColor: "var(--accent-bdr)", minHeight: "2.5rem", minWidth: "min(90vw, 22rem)" }}>
            <AnimatePresence mode="wait">
              <motion.span
                key={pricingFlipIndex}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.4 }}
                className="text-sm sm:text-base font-bold whitespace-nowrap"
                style={{ color: "var(--accent)" }}
              >
                {PRICING_FLIPS[pricingFlipIndex]}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 max-w-4xl mx-auto">
          {[
            { tier: "Starter",    audience: "For Doctors",   price: "\u20B93,999/mo",  note: "200 summaries/month", valueProp: "> 33 hrs/month saved",  features: ["All standard summary APIs", "Webhook delivery", "Online portal", "Email support"],                                           highlight: false },
            { tier: "Growth",     audience: "For Clinics",   price: "\u20B914,999/mo", note: "600 summaries/month",    valueProp: "> 150 hrs/month saved", features: ["All APIs including IPD & Emergency", "Multi-doctor handoffs · shared patient context", "Priority processing", "Priority support"], highlight: true  },
            { tier: "Enterprise", audience: "For Hospitals", price: "Custom",          note: "Unlimited + SLA", valueProp: "Scales with your org",          features: ["All APIs + Patient Summary", "Dedicated deployment option", "Custom integrations", "SLA + dedicated support"],            highlight: false },
          ].map((p, idx) => {
            const isActive = hoveredTier === idx
            return (
            <div
              key={p.tier}
              onMouseEnter={() => setHoveredTier(idx)}
              onMouseLeave={() => setHoveredTier(null)}
              className="relative p-6 sm:p-8 border-2 transition-all duration-300 cursor-pointer"
              style={{
                background:   isActive ? "var(--accent-sub)" : "var(--bg-card)",
                borderColor:  isActive ? "var(--accent)"     : "var(--border)",
                boxShadow:    isActive ? "0 8px 28px rgba(0,0,0,0.12)" : undefined,
                transform:    isActive ? "translateY(-2px)" : "none",
              }}
            >
              {p.highlight && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full"
                  style={{ background: "var(--accent)", color: "#fff", letterSpacing: "0.12em" }}
                >
                  Most Popular
                </div>
              )}
              <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)", letterSpacing: "0.15em" }}>{p.audience}</div>
              <div className="text-xs uppercase tracking-wider mb-1 font-bold" style={{ color: "var(--accent)" }}>{p.tier}</div>
              <div className="text-[1.75rem] sm:text-[2rem] font-semibold mb-1" style={{ ...SERIF, color: "var(--text-primary)" }}>{p.price}</div>
              <div className="text-sm" style={{ color: "var(--text-muted)" }}>{p.note}</div>
              <div
                className="font-medium mb-6 mt-1 transition-all duration-300"
                style={{
                  color: "var(--accent)",
                  fontSize: isActive ? "1rem" : "0.875rem",
                  fontWeight: isActive ? 700 : 600,
                }}
              >
                {p.valueProp}
              </div>
              <ul className="space-y-2 mb-8">
                {p.features.map(f => (
                  <li key={f} className="text-sm flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
                    <span style={{ color: "var(--accent)" }}>&#10003;</span> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => p.tier === "Enterprise" ? openContact("hospital") : navigate("/login")}
                className="w-full py-3 text-sm transition-all duration-300 border"
                style={
                  isActive
                    ? { background: "var(--btn-bg)", color: "var(--btn-txt)", borderColor: "var(--btn-bg)" }
                    : { background: "transparent", color: "var(--text-primary)", borderColor: "var(--text-primary)" }
                }
              >
                {p.tier === "Enterprise" ? "Contact us" : "Get started"}
              </button>
            </div>
          )})}
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-5 sm:px-6 py-16 sm:py-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-[1.75rem] sm:text-[2.5rem] lg:text-[3rem] mb-6 leading-tight" style={{ ...SERIF, color: "var(--text-primary)" }}>
            Scattered information wastes doctors' time and delays patient care.
          </h2>
          <p className="text-base sm:text-lg mb-4 leading-relaxed max-w-2xl mx-auto" style={{ color: "var(--text-secondary)" }}>
            SNOW gathers, organizes, and delivers it in context — in minutes.
          </p>
          <p className="text-base sm:text-[1.25rem] mb-8 sm:mb-10 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Easy to Start. Secure by Design. Built for Scale.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="flex flex-col gap-1.5 items-center sm:items-start">
              <a
                href={SNOW_CLOUD_URL}
                className="px-8 sm:px-10 py-4 sm:py-5 flex items-center gap-2 group shadow-lg text-base sm:text-lg transition-all duration-300"
                style={{ background: "var(--btn-bg)", color: "var(--btn-txt)" }}
                onMouseEnter={e => (e.currentTarget.style.background = "var(--btn-hv)")}
                onMouseLeave={e => (e.currentTarget.style.background = "var(--btn-bg)")}
              >
                <span>Use SNOW Web</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <span className="text-[11px] tracking-wide" style={{ color: "var(--text-muted)" }}>
                For clinicians
              </span>
            </div>
            <div className="flex flex-col gap-1.5 items-center sm:items-start">
              <a
                href={SNOW_API_URL}
                className="px-8 sm:px-10 py-4 sm:py-5 flex items-center gap-2 text-base sm:text-lg transition-all duration-300 border"
                style={{ background: "transparent", color: "var(--text-primary)", borderColor: "var(--text-primary)" }}
              >
                <span>Build with SNOW Platform</span>
              </a>
              <span className="text-[11px] tracking-wide" style={{ color: "var(--text-muted)" }}>
                For developers
              </span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer className="border-t pt-12 pb-8" style={{ borderColor: "var(--border)", background: "var(--bg-section)" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          {/* Top grid: brand + 3 columns */}
          <div className="grid gap-10 md:grid-cols-4 text-sm" style={{ color: "var(--text-muted)" }}>
            <div className="md:col-span-1">
              <div className="text-2xl" style={{ ...SERIF, color: "var(--text-primary)" }}>SNOW</div>
              <p className="mt-3 leading-relaxed">
                Summarization for Medical &amp; Healthcare needs across the patient journeys.
              </p>
              <p className="mt-3 text-xs font-semibold" style={{ color: "var(--accent)" }}>
                Summarise Now. Summarise Well.
              </p>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider mb-3" style={{ color: "var(--text-secondary)" }}>Product</div>
              <ul className="space-y-2">
                <li><a href="#pricing" className="hover:opacity-70 transition-opacity">Pricing</a></li>
                <li><a href="#journey" className="hover:opacity-70 transition-opacity">How it helps</a></li>
              </ul>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider mb-3" style={{ color: "var(--text-secondary)" }}>Legal</div>
              <ul className="space-y-2">
                <li><a href="/privacy" className="hover:opacity-70 transition-opacity">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:opacity-70 transition-opacity">Terms &amp; Conditions</a></li>
                <li><a href="/refund-policy" className="hover:opacity-70 transition-opacity">Refund &amp; Cancellation</a></li>
                <li><a href="/shipping-policy" className="hover:opacity-70 transition-opacity">Shipping &amp; Delivery</a></li>
              </ul>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider mb-3" style={{ color: "var(--text-secondary)" }}>Contact</div>
              <ul className="space-y-2">
                <li><a href="/contact" className="hover:opacity-70 transition-opacity">Contact Us</a></li>
                <li><a href="mailto:snowmedhealth@gmail.com" className="hover:opacity-70 transition-opacity">snowmedhealth@gmail.com</a></li>
                <li><a href="tel:+919908245166" className="hover:opacity-70 transition-opacity">+91 99082 45166</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom bar: copyright + disclaimer + theme switcher */}
          <div
            className="mt-10 pt-6 border-t flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs"
            style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <span>&copy; {new Date().getFullYear()} SNOW · snowmed.health</span>
              <span className="hidden sm:inline opacity-40">·</span>
              <span className="opacity-80">
                SNOW does not provide medical advice, diagnosis, or treatment. For informational and workflow-support purposes only.
              </span>
            </div>
            <div className="flex items-center gap-0.5 border p-0.5 self-start md:self-auto" style={{ borderColor: "var(--border)" }}>
              {(["light", "snow", "dark"] as ThemeId[]).map((id) => (
                <button
                  key={id}
                  onClick={() => setTheme(id)}
                  className="px-2 py-1 text-[10px] tracking-wide transition-all"
                  style={
                    theme === id
                      ? { background: "var(--btn-bg)", color: "var(--btn-txt)" }
                      : { background: "transparent", color: "var(--text-muted)" }
                  }
                >
                  {THEME_LABELS[id]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </footer>

      <ContactDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        tierInterest={contactTier}
        title={contactTier === "hospital" ? "Talk to us about Hospitals" : "Talk to us"}
        subtitle={contactTier === "hospital"
          ? "Multi-department, BAA, dedicated deployment — tell us what you need."
          : undefined}
      />
    </div>
  )
}
